import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { FileSpreadsheet, FileText, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportsPage = () => {
  const { t, language } = useLanguage();
  const currency = language === 'tr' ? '₺' : '$';
  const [reportType, setReportType] = useState('payments');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await axios.get(`${API_URL}/api/reports/${reportType}?${params}`, {
        withCredentials: true
      });
      setReportData(response.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.append('report_type', reportType);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await axios.get(`${API_URL}/api/reports/export/${format}?${params}`, {
        withCredentials: true,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.${format === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report downloaded');
    } catch (error) {
      toast.error(t('errorOccurred'));
    }
  };

  const renderReportTable = () => {
    if (!reportData) return null;

    if (reportType === 'payments') {
      return (
        <div className="border border-gray-200 overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr className="bg-gray-50">
                <th>{t('paymentDate')}</th>
                <th>{t('tenants')}</th>
                <th>{t('amount')}</th>
                <th>{t('paymentMethod')}</th>
                <th>{t('notes')}</th>
              </tr>
            </thead>
            <tbody>
              {reportData.payments?.map((p, idx) => (
                <tr key={idx}>
                  <td>{p.payment_date?.split('T')[0]}</td>
                  <td>{p.tenant_name}</td>
                  <td className="font-mono text-emerald-600">${p.amount}</td>
                  <td className="uppercase text-xs">{p.payment_method}</td>
                  <td>{p.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan="2" className="font-bold">{t('total')}</td>
                <td className="font-mono font-bold text-emerald-600">${reportData.total}</td>
                <td colSpan="2">{reportData.count} payments</td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    }

    if (reportType === 'accruals') {
      return (
        <div className="border border-gray-200 overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr className="bg-gray-50">
                <th>{t('tenants')}</th>
                <th>{t('accrualType')}</th>
                <th>{t('amount')}</th>
                <th>{t('paidAmount')}</th>
                <th>{t('remainingAmount')}</th>
                <th>{t('dueDate')}</th>
                <th>{t('status')}</th>
              </tr>
            </thead>
            <tbody>
              {reportData.accruals?.map((a, idx) => (
                <tr key={idx}>
                  <td>{a.tenant_name}</td>
                  <td className="uppercase text-xs">{a.accrual_type}</td>
                  <td className="font-mono">${a.amount}</td>
                  <td className="font-mono text-emerald-600">${a.paid_amount}</td>
                  <td className="font-mono text-red-600">${a.remaining_amount}</td>
                  <td>{a.due_date?.split('T')[0]}</td>
                  <td className="uppercase text-xs">{a.status}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan="2" className="font-bold">{t('total')}</td>
                <td className="font-mono font-bold">${reportData.summary?.total_amount}</td>
                <td className="font-mono font-bold text-emerald-600">${reportData.summary?.total_paid}</td>
                <td className="font-mono font-bold text-red-600">${reportData.summary?.total_remaining}</td>
                <td colSpan="2">{reportData.summary?.count} accruals</td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    }

    if (reportType === 'units') {
      return (
        <div className="space-y-6">
          {/* Status Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(reportData.status_summary || {}).map(([status, count]) => (
              <div key={status} className="bg-white border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-xs uppercase text-gray-500">{status}</p>
              </div>
            ))}
          </div>

          <div className="border border-gray-200 overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr className="bg-gray-50">
                  <th>{t('unitName')}</th>
                  <th>{t('status')}</th>
                  <th>{t('monthlyRent')}</th>
                  <th>{language === 'tr' ? 'Mevcut Kiracı' : 'Current Tenant'}</th>
                  <th>{language === 'tr' ? 'Sözleşme Bitiş' : 'Contract End'}</th>
                </tr>
              </thead>
              <tbody>
                {reportData.units?.map((u, idx) => (
                  <tr key={idx}>
                    <td className="font-medium">{u.name}</td>
                    <td className="uppercase text-xs">{u.status}</td>
                    <td className="font-mono">${u.monthly_rent || 0}</td>
                    <td>{u.current_tenant || '-'}</td>
                    <td>{u.contract_end_date?.split('T')[0] || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">{t('reports')}</h1>
        <p className="text-gray-500 text-sm mt-1">{language === 'tr' ? 'Rapor oluştur ve dışa aktar' : 'Generate and export reports'}</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium">{language === 'tr' ? 'Rapor Tipi' : 'Report Type'}</label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="rounded-none mt-1" data-testid="report-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="payments">{t('payments')}</SelectItem>
                <SelectItem value="accruals">{t('accruals')}</SelectItem>
                <SelectItem value="units">{t('units')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">{t('startDate')}</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-none mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium">{t('endDate')}</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-none mt-1"
            />
          </div>
          <div className="flex items-end">
            <Button 
              onClick={generateReport}
              disabled={loading}
              className="w-full bg-black text-white rounded-none hover:bg-gray-800"
              data-testid="generate-report-btn"
            >
              {loading ? t('loading') : t('generateReport')}
            </Button>
          </div>
        </div>

        {/* Export Buttons */}
        {reportData && (
          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <Button 
              onClick={() => exportReport('excel')}
              variant="outline"
              className="rounded-none"
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {t('exportExcel')}
            </Button>
            <Button 
              onClick={() => exportReport('pdf')}
              variant="outline"
              className="rounded-none"
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              {t('exportPdf')}
            </Button>
          </div>
        )}
      </div>

      {/* Report Results */}
      {renderReportTable()}
    </div>
  );
};

export default ReportsPage;
